import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Settings2, BookOpen, GraduationCap, Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY_SUBJECT = { name: "", coefficient: 1, hours_per_week: 0, levels: [], class_names: [], responsible_teacher_id: null, active: true };
const EMPTY_CLASS = { name: "", level: "", year: "", max_students: "" };

export default function PedagogySettingsPage() {
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [levels, setLevels] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [subDlg, setSubDlg] = useState(null); // {mode:'create'|'edit', id?}
  const [subForm, setSubForm] = useState(EMPTY_SUBJECT);
  const [clsDlg, setClsDlg] = useState(null);
  const [clsForm, setClsForm] = useState(EMPTY_CLASS);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [s, c, l, t] = await Promise.allSettled([
      api.get("/pedagogy/subjects"),
      api.get("/classes"),
      api.get("/pedagogy/levels"),
      api.get("/users", { params: { role: "teacher" } }),
    ]);
    if (s.status === "fulfilled") setSubjects(s.value.data);
    if (c.status === "fulfilled") setClasses(c.value.data);
    if (l.status === "fulfilled") setLevels(l.value.data);
    if (t.status === "fulfilled") setTeachers(t.value.data);
  };
  useEffect(() => { load(); }, []);

  // ---------- MATIÈRES ----------
  const saveSubject = async () => {
    setSaving(true);
    try {
      const payload = { ...subForm, coefficient: parseFloat(subForm.coefficient) || 1, hours_per_week: parseFloat(subForm.hours_per_week) || 0 };
      if (subDlg.mode === "edit") await api.put(`/pedagogy/subjects/${subDlg.id}`, payload);
      else await api.post("/pedagogy/subjects", payload);
      toast.success(subDlg.mode === "edit" ? "Matière mise à jour" : "Matière créée");
      setSubDlg(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const deleteSubject = async (s) => {
    if (!window.confirm(`Supprimer la matière "${s.name}" ?`)) return;
    try {
      const { data } = await api.delete(`/pedagogy/subjects/${s.id}`);
      toast.success(data.deactivated ? data.detail : "Matière supprimée");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const toggleActive = async (s) => {
    try {
      await api.put(`/pedagogy/subjects/${s.id}`, { ...s, active: !s.active });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  // ---------- CLASSES ----------
  const saveClass = async () => {
    setSaving(true);
    try {
      const payload = { ...clsForm, max_students: clsForm.max_students ? parseInt(clsForm.max_students, 10) : null };
      if (clsDlg.mode === "edit") {
        const { data } = await api.put(`/pedagogy/classes/${clsDlg.id}`, payload);
        const p = data.propagated || {};
        toast.success(`Classe mise à jour${p.students ? ` — ${p.students} élève(s) et ${p.assignments} affectation(s) renommés` : ""}`);
      } else {
        await api.post("/pedagogy/classes", payload);
        toast.success("Classe créée");
      }
      setClsDlg(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const deleteClass = async (c) => {
    if (!window.confirm(`Supprimer la classe "${c.name}" ?`)) return;
    try {
      await api.delete(`/pedagogy/classes/${c.id}`);
      toast.success("Classe supprimée");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="space-y-6" data-testid="pedagogy-settings-page">
      <div>
        <div className="chip mb-2"><Settings2 className="h-3 w-3" /><span>PARAMÉTRAGE PÉDAGOGIQUE</span></div>
        <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Matières, classes & niveaux</h1>
        <p className="text-slate-500 mt-1">Structure pédagogique de l&apos;établissement, librement adaptable sans intervention technique.</p>
      </div>

      <Tabs defaultValue="subjects">
        <TabsList>
          <TabsTrigger value="subjects" data-testid="tab-subjects"><BookOpen className="h-4 w-4 mr-1" />Matières</TabsTrigger>
          <TabsTrigger value="classes" data-testid="tab-classes"><GraduationCap className="h-4 w-4 mr-1" />Classes & niveaux</TabsTrigger>
        </TabsList>

        {/* ===== MATIÈRES ===== */}
        <TabsContent value="subjects" className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Button onClick={() => { setSubForm(EMPTY_SUBJECT); setSubDlg({ mode: "create" }); }} className="bg-[#1E3A8A] text-white gap-1" data-testid="add-subject-btn"><Plus className="h-4 w-4" />Nouvelle matière</Button>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-left">Matière</th>
                  <th className="px-4 py-3 text-center">Coef.</th>
                  <th className="px-4 py-3 text-center">h/sem.</th>
                  <th className="px-4 py-3 text-left">Niveaux / Classes</th>
                  <th className="px-4 py-3 text-left">Enseignant responsable</th>
                  <th className="px-4 py-3 text-center">Active</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {subjects.map(s => (
                  <tr key={s.id} className={`border-t border-slate-100 ${!s.active ? "opacity-50" : ""}`} data-testid={`subject-row-${s.id}`}>
                    <td className="px-4 py-2 font-semibold text-[#1E3A8A]">{s.name}</td>
                    <td className="px-4 py-2 text-center tabular-nums">{s.coefficient}</td>
                    <td className="px-4 py-2 text-center tabular-nums">{s.hours_per_week || "—"}</td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-1">
                        {(s.levels || []).map(l => <span key={l} className="text-xs px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 font-semibold">{l}</span>)}
                        {(s.class_names || []).map(c => <span key={c} className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-[#1E3A8A] font-semibold">{c}</span>)}
                        {!(s.levels || []).length && !(s.class_names || []).length && <span className="text-xs text-slate-400">Toutes</span>}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-xs">{s.responsible_teacher_name || "—"}</td>
                    <td className="px-4 py-2 text-center"><Switch checked={!!s.active} onCheckedChange={() => toggleActive(s)} data-testid={`subject-active-${s.id}`} /></td>
                    <td className="px-4 py-2 text-right">
                      <Button size="sm" variant="ghost" onClick={() => { setSubForm({ ...EMPTY_SUBJECT, ...s }); setSubDlg({ mode: "edit", id: s.id }); }} data-testid={`subject-edit-${s.id}`}><Pencil className="h-3 w-3" /></Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteSubject(s)} data-testid={`subject-del-${s.id}`}><Trash2 className="h-3 w-3" /></Button>
                    </td>
                  </tr>
                ))}
                {subjects.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Aucune matière paramétrée — créez la première.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ===== CLASSES ===== */}
        <TabsContent value="classes" className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Button onClick={() => { setClsForm(EMPTY_CLASS); setClsDlg({ mode: "create" }); }} className="bg-[#1E3A8A] text-white gap-1" data-testid="add-class-btn"><Plus className="h-4 w-4" />Nouvelle classe</Button>
          </div>
          {levels.map(level => {
            const rows = classes.filter(c => c.level === level);
            if (!rows.length) return null;
            return (
              <div key={level} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-5 py-3 bg-slate-50 border-b border-slate-100 font-heading font-bold text-[#1E3A8A]">{level} <span className="text-xs font-normal text-slate-400">({rows.length} classe{rows.length > 1 ? "s" : ""})</span></div>
                <table className="w-full text-sm">
                  <tbody>
                    {rows.map(c => (
                      <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`class-row-${c.id}`}>
                        <td className="px-4 py-2 font-semibold">{c.name}</td>
                        <td className="px-4 py-2 text-xs text-slate-500">{c.year || "—"}</td>
                        <td className="px-4 py-2 text-xs" data-testid={`class-capacity-${c.id}`}>
                          {c.max_students ? (
                            <span className={`font-bold px-2 py-0.5 rounded-full ${(c.students_count || 0) >= c.max_students ? "bg-red-100 text-red-700" : (c.students_count || 0) >= c.max_students * 0.9 ? "bg-orange-100 text-orange-700" : "bg-green-100 text-green-700"}`}>
                              {c.students_count || 0}/{c.max_students} élèves{(c.students_count || 0) >= c.max_students ? " — PLEINE" : ""}
                            </span>
                          ) : (
                            <span className="text-slate-400">{c.students_count || 0} élèves · sans limite</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <Button size="sm" variant="ghost" onClick={() => { setClsForm({ name: c.name, level: c.level, year: c.year || "", max_students: c.max_students || "" }); setClsDlg({ mode: "edit", id: c.id }); }} data-testid={`class-edit-${c.id}`}><Pencil className="h-3 w-3" /></Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteClass(c)} data-testid={`class-del-${c.id}`}><Trash2 className="h-3 w-3" /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
          {classes.filter(c => !levels.includes(c.level)).length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-4">
              {classes.filter(c => !levels.includes(c.level)).map(c => (
                <div key={c.id} className="flex justify-between items-center text-sm py-1">{c.name} ({c.level || "sans niveau"})</div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* ===== DIALOG MATIÈRE ===== */}
      <Dialog open={!!subDlg} onOpenChange={(o) => !o && setSubDlg(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>{subDlg?.mode === "edit" ? "Modifier la matière" : "Nouvelle matière"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nom de la matière *</Label>
              <Input value={subForm.name} onChange={e => setSubForm({ ...subForm, name: e.target.value })} placeholder="Ex : Physique-Chimie" data-testid="sub-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Coefficient</Label>
                <Input type="number" step="0.5" min="0" value={subForm.coefficient} onChange={e => setSubForm({ ...subForm, coefficient: e.target.value })} data-testid="sub-coef" />
              </div>
              <div>
                <Label>Volume horaire (h/semaine)</Label>
                <Input type="number" step="0.5" min="0" value={subForm.hours_per_week} onChange={e => setSubForm({ ...subForm, hours_per_week: e.target.value })} data-testid="sub-hours" />
              </div>
            </div>
            <div>
              <Label className="mb-1 block">Niveaux concernés</Label>
              <div className="flex flex-wrap gap-3">
                {levels.map(l => (
                  <label key={l} className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <Checkbox checked={subForm.levels.includes(l)} onCheckedChange={() => setSubForm(f => ({ ...f, levels: f.levels.includes(l) ? f.levels.filter(x => x !== l) : [...f.levels, l] }))} data-testid={`sub-level-${l}`} />
                    {l}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="mb-1 block">Classes concernées (optionnel — vide = tout le niveau)</Label>
              <div className="grid grid-cols-2 gap-1.5 max-h-36 overflow-y-auto border border-slate-200 rounded-lg p-2">
                {classes.map(c => (
                  <label key={c.id} className="flex items-center gap-1.5 text-xs cursor-pointer">
                    <Checkbox checked={subForm.class_names.includes(c.name)} onCheckedChange={() => setSubForm(f => ({ ...f, class_names: f.class_names.includes(c.name) ? f.class_names.filter(x => x !== c.name) : [...f.class_names, c.name] }))} />
                    {c.name}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>Enseignant responsable</Label>
              <Select value={subForm.responsible_teacher_id || "none"} onValueChange={v => setSubForm({ ...subForm, responsible_teacher_id: v === "none" ? null : v })}>
                <SelectTrigger data-testid="sub-teacher"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Aucun —</SelectItem>
                  {teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.full_name || t.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubDlg(null)}>Annuler</Button>
            <Button onClick={saveSubject} disabled={saving} className="bg-[#1E3A8A] text-white" data-testid="sub-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== DIALOG CLASSE ===== */}
      <Dialog open={!!clsDlg} onOpenChange={(o) => !o && setClsDlg(null)}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader><DialogTitle>{clsDlg?.mode === "edit" ? "Modifier la classe" : "Nouvelle classe"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {clsDlg?.mode === "edit" && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-[#1E3A8A]">
                En cas de renommage, les élèves inscrits, les affectations des enseignants et les matières liées sont automatiquement mis à jour (intégrité des données).
              </div>
            )}
            <div>
              <Label>Nom de la classe *</Label>
              <Input value={clsForm.name} onChange={e => setClsForm({ ...clsForm, name: e.target.value })} placeholder="Ex : Primaire CM1 B" data-testid="cls-name" />
            </div>
            <div>
              <Label>Niveau *</Label>
              <Input list="levels-list" value={clsForm.level} onChange={e => setClsForm({ ...clsForm, level: e.target.value })} placeholder="Ex : Primaire, Collège, Lycée..." data-testid="cls-level" />
              <datalist id="levels-list">{levels.map(l => <option key={l} value={l} />)}</datalist>
            </div>
            <div>
              <Label>Année scolaire</Label>
              <Input value={clsForm.year} onChange={e => setClsForm({ ...clsForm, year: e.target.value })} placeholder="Ex : 2026-2027" data-testid="cls-year" />
            </div>
            <div>
              <Label>Effectif maximum (vide = illimité)</Label>
              <Input type="number" min="1" value={clsForm.max_students} onChange={e => setClsForm({ ...clsForm, max_students: e.target.value })} placeholder="Ex : 35" data-testid="cls-max-students" />
              <p className="text-xs text-slate-400 mt-1">Les inscriptions seront bloquées une fois cet effectif atteint.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClsDlg(null)}>Annuler</Button>
            <Button onClick={saveClass} disabled={saving} className="bg-[#1E3A8A] text-white" data-testid="cls-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
